import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, message, Popconfirm, Tag, Modal, Menu, Dropdown, Select, Switch, Alert, Typography } from 'antd';
import { PlusOutlined, EditOutlined, CheckOutlined, StopOutlined, OrderedListOutlined, MoreOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api, { monthlyMetricService } from '../../services/api';

const formatColor = (color) => {
    if (!color) return '#000000';
    return color.startsWith('#') ? color : `#${color}`;
};

const MonthlyMetrics = () => {
    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortModalVisible, setSortModalVisible] = useState(false);
    const [sortableMetrics, setSortableMetrics] = useState([]);
    const [savingSortOrder, setSavingSortOrder] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [brands, setBrands] = useState([]);
    const [metricVisibility, setMetricVisibility] = useState({});
    const [targetBrands, setTargetBrands] = useState([]);
    // Template filtreleme için yeni state değişkenleri
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [summaryModalVisible, setSummaryModalVisible] = useState(false);
    const navigate = useNavigate();

    const fetchMetrics = useCallback(async () => {
        try {
            setLoading(true);
            // Minimal mod ile sadece liste görünümü için gerekli verileri getir
            const response = await api.get('/monthly-metrics', {
                params: { minimal: 'true' }
            });
            if (response.data) {
                setMetrics(response.data);
            }
        } catch (error) {
            console.error('Metrikler yüklenirken hata:', error);
            message.error('Metrikler yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, []);
    
    const fetchBrands = useCallback(async () => {
        try {
            const response = await api.get('/brands', {
                params: { minimal: 'true' }
            });
            if (response.data && Array.isArray(response.data)) {
                const sortedBrands = response.data
                    .filter(brand => brand && brand.name)
                    .sort((a, b) => a.name.localeCompare(b.name));
                setBrands(sortedBrands);
                setFilteredBrands(sortedBrands);
                
                // Template listesini oluştur
                const uniqueTemplates = [...new Set(sortedBrands
                    .filter(brand => brand.template)
                    .map(brand => brand.template))]
                    .sort();
                setTemplates(uniqueTemplates);
                
                // İlk markayı varsayılan olarak seç
                if (sortedBrands.length > 0 && !selectedBrand) {
                    setSelectedBrand(sortedBrands[0].id);
                }
            }
        } catch (error) {
            console.error('Markalar yüklenirken hata:', error);
            message.error('Markalar yüklenirken bir hata oluştu');
        }
    }, [selectedBrand]);

    useEffect(() => {
        fetchMetrics();
        fetchBrands();
    }, [fetchMetrics, fetchBrands]);

    const handleEdit = (record) => {
        navigate(`/monthly-metrics/edit/${record.id}`);
    };

    const handleDelete = async (record) => {
        try {
            if (record.isDeleted) {
                await api.post(`/monthly-metrics/${record.id}/activate`);
                message.success('Metrik başarıyla aktife alındı');
            } else {
                await api.delete(`/monthly-metrics/${record.id}`);
                message.success('Metrik başarıyla pasife alındı');
            }
            fetchMetrics();
        } catch (error) {
            console.error('Metrik durumu değiştirilirken hata:', error);
            message.error('Metrik durumu değiştirilirken bir hata oluştu');
        }
    };

    // Template değiştiğinde markaları filtrele
    useEffect(() => {
        if (!selectedTemplate) {
            // Template seçili değilse tüm markaları göster
            setFilteredBrands(brands);
            // Tüm markaları hedef olarak seç
            setTargetBrands(brands.map(brand => brand.id));
        } else {
            // Seçili template'e göre markaları filtrele
            const filtered = brands.filter(brand => brand.template === selectedTemplate);
            setFilteredBrands(filtered);
            // Filtrelenmiş markaları hedef olarak seç
            setTargetBrands(filtered.map(brand => brand.id));
            
            // Eğer seçili marka filtrelenmiş markalarda yoksa, ilk filtrelenmiş markayı seç
            if (filtered.length > 0 && !filtered.some(brand => brand.id === selectedBrand)) {
                setSelectedBrand(filtered[0].id);
            }
        }
    }, [selectedTemplate, brands, selectedBrand]);

    // Seçilen marka için metrik sıralama ve görünürlük bilgilerini getir
    const fetchBrandMetricDetails = async (brandId, isSummary = false) => {
        try {
            setLoading(true);
            let response;
            
            if (isSummary) {
                // Özet metrikler için API çağrısı yaparken, sıralama modalı için gizli metrikleri de getir
                // includeHidden=true ile tüm metrikler (görünür ve gizli) getirilecek
                response = await monthlyMetricService.getMonthlyMetricSummary(brandId, true);
                if (response && Array.isArray(response)) {
                    // Özet metrikler düzenlenmiş formatıyla geliyor
                    const sortableItems = response.map(metric => ({
                        id: metric.id,
                        name: metric.name,
                        displayOrder: metric.displayOrder !== undefined ? metric.displayOrder : 0,
                        isVisible: metric.isVisible !== undefined ? metric.isVisible : true,
                        description: metric.description,
                        isAutoCalculated: metric.isAutoCalculated,
                        color: metric.color
                    }));
                    
                    setSortableMetrics(sortableItems);
                    
                    // Görünürlük durumlarını ayarla
                    const visibilityMap = {};
                    response.forEach(metric => {
                        visibilityMap[metric.id] = metric.isVisible !== undefined ? metric.isVisible : true;
                    });
                    setMetricVisibility(visibilityMap);
                }
            } else {
                response = await api.get(`/monthly-metrics/brands/${brandId}`, {
                    params: { date: new Date().toISOString().substring(0, 7) }
                });
                
                if (response.data && Array.isArray(response.data)) {
                    // Metrikleri displayOrder'a göre sırala
                    const sortedMetrics = response.data.sort((a, b) => {
                        const orderA = a.displayOrder !== undefined ? a.displayOrder : Number.MAX_SAFE_INTEGER;
                        const orderB = b.displayOrder !== undefined ? b.displayOrder : Number.MAX_SAFE_INTEGER;
                        return orderA - orderB;
                    });
                    
                    // Sıralanabilir metrikleri oluştur
                    const sortableItems = sortedMetrics.map(metric => ({
                        id: metric.id,
                        name: metric.name,
                        displayOrder: metric.displayOrder !== undefined ? metric.displayOrder : 0,
                        isVisible: metric.isVisible !== undefined ? metric.isVisible : true,
                        description: metric.description,
                        isAutoCalculated: metric.isAutoCalculated,
                        color: metric.color
                    }));
                    
                    setSortableMetrics(sortableItems);
                    
                    // Görünürlük durumlarını ayarla
                    const visibilityMap = {};
                    sortedMetrics.forEach(metric => {
                        visibilityMap[metric.id] = metric.isVisible !== undefined ? metric.isVisible : true;
                    });
                    setMetricVisibility(visibilityMap);
                }
            }
        } catch (error) {
            console.error('Marka metrik detayları yüklenirken hata:', error);
            message.error('Marka metrik detayları yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Template seçim değişikliğini işle
    const handleTemplateChange = (value) => {
        setSelectedTemplate(value);
    };

    // Sıralama modali işlemleri
    const showSortModal = () => {
        if (!selectedBrand) {
            message.warning('Lütfen önce bir marka seçin');
            return;
        }
        
        // Seçili template varsa, o template'e ait markaları seç
        // Yoksa tüm markaları başlangıçta seç
        if (selectedTemplate) {
            const templateBrands = brands.filter(brand => brand.template === selectedTemplate);
            setTargetBrands(templateBrands.map(brand => brand.id));
        } else {
            setTargetBrands(brands.map(brand => brand.id));
        }
        
        // Seçilen marka için normal metrik detaylarını getir
        fetchBrandMetricDetails(selectedBrand, false);
        setSortModalVisible(true);
    };

    // Özet metrikler sıralama modali işlemleri
    const showSummaryModal = () => {
        if (!selectedBrand) {
            message.warning('Lütfen önce bir marka seçin');
            return;
        }
        
        // Seçili template varsa, o template'e ait markaları seç
        // Yoksa tüm markaları başlangıçta seç
        if (selectedTemplate) {
            const templateBrands = brands.filter(brand => brand.template === selectedTemplate);
            setTargetBrands(templateBrands.map(brand => brand.id));
        } else {
            setTargetBrands(brands.map(brand => brand.id));
        }
        
        // Seçilen marka için özet metrik detaylarını getir
        fetchBrandMetricDetails(selectedBrand, true);
        setSummaryModalVisible(true);
    };

    // Sürükle bırak işlemi sonunda
    const handleDragEnd = (result) => {
        const { destination, source } = result;
        
        // Eğer hedef yoksa (sürükleme iptal edildiyse) veya aynı konuma bırakıldıysa işlem yapma
        if (!destination || 
            (destination.index === source.index && destination.droppableId === source.droppableId)) {
            return;
        }
        
        const newItems = Array.from(sortableMetrics);
        const [movedItem] = newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, movedItem);
        
        // DisplayOrder değerlerini güncelle
        const updatedItems = newItems.map((item, index) => ({
            ...item,
            displayOrder: index
        }));
        
        setSortableMetrics(updatedItems);
    };
    
    // Görünürlük değiştirme işlemi
    const handleVisibilityChange = (metricId, checked) => {
        setMetricVisibility(prev => ({
            ...prev,
            [metricId]: checked
        }));
    };
    
    // Tüm metriklerin görünürlüğünü ayarla
    const handleSetAllVisibility = (visibleState) => {
        const newVisibility = {};
        sortableMetrics.forEach(metric => {
            newVisibility[metric.id] = visibleState;
        });
        setMetricVisibility(newVisibility);
    };

    // Seçili markalar için sıralama ve görünürlük ayarlarını kaydet
    const saveSortOrder = async (isSummary = false) => {
        if (!selectedBrand) {
            message.warning('Lütfen önce bir referans marka seçin');
            return;
        }
        
        if (targetBrands.length === 0) {
            message.warning('Lütfen en az bir hedef marka seçin');
            return;
        }
        
        // Template seçili ise, kullanıcıya bilgi ver
        if (selectedTemplate) {
            const templateBrands = brands.filter(brand => brand.template === selectedTemplate);
            if (templateBrands.length > 0 && !targetBrands.some(id => templateBrands.some(brand => brand.id === id))) {
                const confirmResult = await new Promise(resolve => {
                    Modal.confirm({
                        title: 'Template Uyarısı',
                        content: `Seçtiğiniz ${selectedTemplate} template'ine ait hiçbir marka hedef listesinde bulunmuyor. Devam etmek istiyor musunuz?`,
                        okText: 'Devam Et',
                        cancelText: 'İptal',
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false)
                    });
                });
                
                if (!confirmResult) {
                    return;
                }
            }
        }
        
        try {
            setSavingSortOrder(true);
            
            // Metrik sıralama ve görünürlük bilgilerini hazırla
            const updatedMetrics = sortableMetrics.map(metric => ({
                id: parseInt(metric.id),
                displayOrder: metric.displayOrder,
                isVisible: metricVisibility[metric.id]
            }));
            
            // Önce kullanıcıyı bilgilendir
            message.loading({
                content: 'Seçili markalar için metrik sıralama ve görünürlük ayarları güncelleniyor...',
                key: 'sortOrderLoading',
                duration: 0
            });
            
            let result;
            
            if (isSummary) {
                // Özet metrik düzeni
                if (targetBrands.length === 1) {
                    // Tek marka için
                    result = await monthlyMetricService.updateSummaryMetricDisplayOrder(selectedBrand, updatedMetrics);
                } else {
                    // Çoklu marka için - özet görünüm için isSummaryUpdate=true gönderiyoruz
                    result = await monthlyMetricService.updateAllBrandsMetricDisplayOrder(updatedMetrics, targetBrands, true);
                }
            } else {
                // Normal metrik düzeni
                result = await monthlyMetricService.updateAllBrandsMetricDisplayOrder(updatedMetrics, targetBrands);
            }
            
            if (result.success) {
                message.success({
                    content: `Metrik sıralaması ve görünürlük ayarları ${result.brandCount || targetBrands.length} marka için başarıyla güncellendi`,
                    key: 'sortOrderLoading',
                    duration: 3
                });
                
                // Modal'ı kapat
                if (isSummary) {
                    setSummaryModalVisible(false);
                } else {
                    setSortModalVisible(false);
                }
                
                // Güncellenmiş metrikleri getir
                await fetchMetrics();
            } else {
                throw new Error(result.error || 'Bir hata oluştu');
            }
        } catch (error) {
            console.error('Sıralama ve görünürlük ayarları kaydetme hatası:', error);
            message.error({
                content: 'Ayarlar kaydedilirken bir hata oluştu',
                key: 'sortOrderLoading'
            });
        } finally {
            setSavingSortOrder(false);
        }
    };

    // Normal metrikler sıralama modalı render
    const renderSortModal = () => (
        <Modal
            title="Aylık Metrik Sıralama ve Görünürlük Ayarları"
            open={sortModalVisible}
            onCancel={() => setSortModalVisible(false)}
            width={800}
            footer={[
                <Button key="cancel" onClick={() => setSortModalVisible(false)}>
                    İptal
                </Button>,
                <Button 
                    key="save" 
                    type="primary" 
                    loading={savingSortOrder}
                    onClick={() => saveSortOrder(false)}
                >
                    Seçili Markalara Uygula
                </Button>
            ]}
        >
            <div style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
                    <Space align="start">
                        <div>
                            <div style={{ marginBottom: 8 }}>Referans Marka Seçin:</div>
                            <Select
                                value={selectedBrand}
                                onChange={(value) => {
                                    setSelectedBrand(value);
                                    fetchBrandMetricDetails(value, false);
                                }}
                                style={{ width: 200 }}
                                placeholder="Marka seçin"
                                showSearch
                                filterOption={(input, option) => {
                                    const brand = filteredBrands.find(b => b.id === option.value);
                                    if (!brand) return false;
                                    
                                    const searchText = input.toLowerCase();
                                    const brandName = (brand.name || '').toLowerCase();
                                    const brandTemplate = (brand.template || '').toLowerCase();
                                    
                                    return brandName.includes(searchText) || brandTemplate.includes(searchText);
                                }}
                            >
                                {filteredBrands.map(brand => (
                                    <Select.Option key={brand.id} value={brand.id}>
                                        {brand.name} {brand.template ? `(${brand.template})` : ''}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        
                        <div>
                            <div style={{ marginBottom: 8 }}>Template Filtresi:</div>
                            <Select
                                value={selectedTemplate}
                                onChange={handleTemplateChange}
                                style={{ width: 200 }}
                                placeholder="Template seçin"
                                allowClear
                            >
                                {templates.map(template => (
                                    <Select.Option key={template} value={template}>
                                        {template}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </Space>
                    
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <span>Ayarları Uygulanacak Markalar:</span>
                        <Select
                            mode="multiple"
                            placeholder="Markalar seçin"
                            value={targetBrands}
                            onChange={setTargetBrands}
                            style={{ width: '100%' }}
                            showSearch
                            filterOption={(input, option) => {
                                const brand = filteredBrands.find(b => b.id === option.value);
                                if (!brand) return false;
                                
                                const searchText = input.toLowerCase();
                                const brandName = (brand.name || '').toLowerCase();
                                const brandTemplate = (brand.template || '').toLowerCase();
                                
                                return brandName.includes(searchText) || brandTemplate.includes(searchText);
                            }}
                        >
                            {filteredBrands.map(brand => (
                                <Select.Option key={brand.id} value={brand.id}>
                                    {brand.name} {brand.template ? `(${brand.template})` : ''}
                                </Select.Option>
                            ))}
                        </Select>
                        <Space>
                            <Button 
                                size="small" 
                                onClick={() => setTargetBrands(filteredBrands.map(brand => brand.id))}
                            >
                                Tümünü Seç
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setTargetBrands([])}
                            >
                                Tümünü Temizle
                            </Button>
                            {selectedTemplate && (
                                <Tag color="blue">{selectedTemplate} template'i seçili</Tag>
                            )}
                        </Space>
                    </Space>
                    
                    <Space>
                        <Button onClick={() => handleSetAllVisibility(true)}>
                            Tümünü Göster
                        </Button>
                        <Button onClick={() => handleSetAllVisibility(false)}>
                            Tümünü Gizle
                        </Button>
                    </Space>
                    
                    <Alert
                        message="Aylık Metrik Sıralama ve Görünürlük"
                        description={
                            <div>
                                <p>Metrikleri sıralamak için sürükleyip bırakın. Görünürlük ayarlarını değiştirmek için anahtarları kullanın.</p>
                            </div>
                        }
                        type="info"
                        showIcon
                    />
                </Space>
            </div>
            
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="metrics-list">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {sortableMetrics.map((metric, index) => (
                                <Draggable 
                                    key={metric.id.toString()} 
                                    draggableId={metric.id.toString()} 
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                userSelect: 'none',
                                                padding: 16,
                                                margin: '0 0 8px 0',
                                                backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: 4,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ marginRight: 16, fontWeight: 'bold', width: 30 }}>
                                                    {index + 1}
                                                </div>
                                                <div style={{ 
                                                    width: 24, 
                                                    height: 24, 
                                                    backgroundColor: formatColor(metric.color), 
                                                    borderRadius: '50%',
                                                    border: '1px solid #ddd',
                                                    marginRight: 16
                                                }} />
                                                <div>
                                                    <div style={{ fontWeight: 'bold' }}>{metric.name}</div>
                                                    <div style={{ fontSize: '12px', color: '#888' }}>
                                                        {metric.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <Space>
                                                    <span style={{ marginRight: 8 }}>
                                                        {metricVisibility[metric.id] === true ? 
                                                            <Tag color="success">Görünür</Tag> : 
                                                            <Tag color="default">Gizli</Tag>
                                                        }
                                                    </span>
                                                    <Switch
                                                        checked={metricVisibility[metric.id] === true}
                                                        onChange={(checked) => handleVisibilityChange(metric.id, checked)}
                                                        checkedChildren={<EyeOutlined />}
                                                        unCheckedChildren={<EyeInvisibleOutlined />}
                                                    />
                                                </Space>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </Modal>
    );

    // Özet metrikler sıralama modalı render
    const renderSummaryModal = () => (
        <Modal
            title="Özet Metrik Sıralama ve Görünürlük Ayarları"
            open={summaryModalVisible}
            onCancel={() => setSummaryModalVisible(false)}
            width={800}
            footer={[
                <Button key="cancel" onClick={() => setSummaryModalVisible(false)}>
                    İptal
                </Button>,
                <Button 
                    key="save" 
                    type="primary" 
                    loading={savingSortOrder}
                    onClick={() => saveSortOrder(true)}
                >
                    Seçili Markalara Uygula
                </Button>
            ]}
        >
            <div style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
                    <Space align="start">
                        <div>
                            <div style={{ marginBottom: 8 }}>Referans Marka Seçin:</div>
                            <Select
                                value={selectedBrand}
                                onChange={(value) => {
                                    setSelectedBrand(value);
                                    fetchBrandMetricDetails(value, true);
                                }}
                                style={{ width: 200 }}
                                placeholder="Marka seçin"
                                showSearch
                                filterOption={(input, option) => {
                                    const brand = filteredBrands.find(b => b.id === option.value);
                                    if (!brand) return false;
                                    
                                    const searchText = input.toLowerCase();
                                    const brandName = (brand.name || '').toLowerCase();
                                    const brandTemplate = (brand.template || '').toLowerCase();
                                    
                                    return brandName.includes(searchText) || brandTemplate.includes(searchText);
                                }}
                            >
                                {filteredBrands.map(brand => (
                                    <Select.Option key={brand.id} value={brand.id}>
                                        {brand.name} {brand.template ? `(${brand.template})` : ''}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        
                        <div>
                            <div style={{ marginBottom: 8 }}>Template Filtresi:</div>
                            <Select
                                value={selectedTemplate}
                                onChange={handleTemplateChange}
                                style={{ width: 200 }}
                                placeholder="Template seçin"
                                allowClear
                            >
                                {templates.map(template => (
                                    <Select.Option key={template} value={template}>
                                        {template}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </Space>
                    
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <span>Ayarları Uygulanacak Markalar:</span>
                        <Select
                            mode="multiple"
                            placeholder="Markalar seçin"
                            value={targetBrands}
                            onChange={setTargetBrands}
                            style={{ width: '100%' }}
                            showSearch
                            filterOption={(input, option) => {
                                const brand = filteredBrands.find(b => b.id === option.value);
                                if (!brand) return false;
                                
                                const searchText = input.toLowerCase();
                                const brandName = (brand.name || '').toLowerCase();
                                const brandTemplate = (brand.template || '').toLowerCase();
                                
                                return brandName.includes(searchText) || brandTemplate.includes(searchText);
                            }}
                        >
                            {filteredBrands.map(brand => (
                                <Select.Option key={brand.id} value={brand.id}>
                                    {brand.name} {brand.template ? `(${brand.template})` : ''}
                                </Select.Option>
                            ))}
                        </Select>
                        <Space>
                            <Button 
                                size="small" 
                                onClick={() => setTargetBrands(filteredBrands.map(brand => brand.id))}
                            >
                                Tümünü Seç
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setTargetBrands([])}
                            >
                                Tümünü Temizle
                            </Button>
                            {selectedTemplate && (
                                <Tag color="blue">{selectedTemplate} template'i seçili</Tag>
                            )}
                        </Space>
                    </Space>
                    
                    <Space>
                        <Button onClick={() => handleSetAllVisibility(true)}>
                            Tümünü Göster
                        </Button>
                        <Button onClick={() => handleSetAllVisibility(false)}>
                            Tümünü Gizle
                        </Button>
                    </Space>
                    
                    <Alert
                        message="Özet Metrik Sıralama ve Görünürlük"
                        description={
                            <div>
                                <p><strong>Not:</strong> Burada yapılan değişiklikler, özet metrik görünümünü etkiler ve normal metriklerden bağımsızdır.</p>
                                <p>Metrikleri sıralamak için sürükleyip bırakın. Görünürlük ayarlarını değiştirmek için anahtarları kullanın.</p>
                            </div>
                        }
                        type="info"
                        showIcon
                    />
                </Space>
            </div>
            
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="metrics-list">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {sortableMetrics.map((metric, index) => (
                                <Draggable 
                                    key={metric.id.toString()} 
                                    draggableId={metric.id.toString()} 
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                userSelect: 'none',
                                                padding: 16,
                                                margin: '0 0 8px 0',
                                                backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: 4,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ marginRight: 16, fontWeight: 'bold', width: 30 }}>
                                                    {index + 1}
                                                </div>
                                                <div style={{ 
                                                    width: 24, 
                                                    height: 24, 
                                                    backgroundColor: formatColor(metric.color), 
                                                    borderRadius: '50%',
                                                    border: '1px solid #ddd',
                                                    marginRight: 16
                                                }} />
                                                <div>
                                                    <div style={{ fontWeight: 'bold' }}>{metric.name}</div>
                                                    <div style={{ fontSize: '12px', color: '#888' }}>
                                                        {metric.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <Space>
                                                    <span style={{ marginRight: 8 }}>
                                                        {metricVisibility[metric.id] === true ? 
                                                            <Tag color="success">Görünür</Tag> : 
                                                            <Tag color="default">Gizli</Tag>
                                                        }
                                                    </span>
                                                    <Switch
                                                        checked={metricVisibility[metric.id] === true}
                                                        onChange={(checked) => handleVisibilityChange(metric.id, checked)}
                                                        checkedChildren={<EyeOutlined />}
                                                        unCheckedChildren={<EyeInvisibleOutlined />}
                                                    />
                                                </Space>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </Modal>
    );

    const moreMenu = (
        <Menu>
            <Menu.Item key="sort" onClick={showSortModal} icon={<OrderedListOutlined />}>
                Aylık Metrik Sıralama ve Görünürlük Ayarları
            </Menu.Item>
            <Menu.Item key="summary" onClick={showSummaryModal} icon={<OrderedListOutlined />}>
                Özet Metrik Sıralama ve Görünürlük Ayarları
            </Menu.Item>
        </Menu>
    );

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: 'Metrik Adı',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: 'Açıklama',
            dataIndex: 'description',
            key: 'description',
            render: (text) => text || '-'
        },
        {
            title: 'Veri Tipi',
            dataIndex: 'dataType',
            key: 'dataType',
            render: (text) => text === 'text' ? 'Metin' : 'Sayısal'
        },
        {
            title: 'Otomatik Hesaplama',
            dataIndex: 'isAutoCalculated',
            key: 'isAutoCalculated',
            render: (value) => (
                <Tag color={value ? '#87d068' : '#f50'}>
                    {value ? 'Evet' : 'Hayır'}
                </Tag>
            )
        },
        {
            title: 'Renk',
            dataIndex: 'color',
            key: 'color',
            render: (color) => (
                <div 
                    style={{ 
                        width: 24, 
                        height: 24, 
                        backgroundColor: formatColor(color), 
                        borderRadius: '50%',
                        border: '1px solid #ddd'
                    }} 
                />
            )
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Düzenle
                    </Button>
                    <Popconfirm
                        title={record.isDeleted ? 
                            "Metriği aktife almak istediğinizden emin misiniz?" : 
                            "Metriği pasife almak istediğinizden emin misiniz?"}
                        description={record.isDeleted ? 
                            "Metrik aktife alındığında tekrar kullanılabilir." :
                            "Metrik pasife alındığında kullanılamayacak ancak mevcut veriler korunacaktır."}
                        onConfirm={() => handleDelete(record)}
                        okText="Evet"
                        cancelText="Hayır"
                        okButtonProps={{ danger: !record.isDeleted }}
                    >
                        <Button 
                            type="primary" 
                            danger={!record.isDeleted}
                            icon={record.isDeleted ? <CheckOutlined /> : <StopOutlined />}
                        >
                            {record.isDeleted ? 'Aktife Al' : 'Pasife Al'}
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Aylık Metrikler</h2>
                <Space>
                    <Space>
                        <span>Template Filtresi:</span>
                        <Select
                            value={selectedTemplate}
                            onChange={handleTemplateChange}
                            style={{ width: 200 }}
                            placeholder="Template seçin"
                            allowClear
                        >
                            {templates.map(template => (
                                <Select.Option key={template} value={template}>
                                    {template}
                                </Select.Option>
                            ))}
                        </Select>
                    </Space>
                    <Dropdown overlay={moreMenu} placement="bottomRight">
                        <Button icon={<MoreOutlined />}>İşlemler</Button>
                    </Dropdown>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/monthly-metrics/new')}
                    >
                        Yeni Metrik Ekle
                    </Button>
                </Space>
            </div>
            
            {selectedTemplate && (
                <Alert
                    message={`${selectedTemplate} template'ine göre filtreleniyor`}
                    type="info"
                    showIcon
                    closable
                    onClose={() => setSelectedTemplate(null)}
                    style={{ marginBottom: 16 }}
                />
            )}
            <Table
                columns={columns}
                dataSource={metrics}
                rowKey="id"
                loading={loading}
                pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Toplam ${total} metrik`
                }}
                footer={() => (
                    <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button 
                            type="primary" 
                            icon={<OrderedListOutlined />} 
                            onClick={showSortModal}
                        >
                            Aylık Metrik Sıralama ve Görünürlük
                        </Button>
                        <Button 
                            type="primary" 
                            style={{ backgroundColor: '#8C64D8' }}
                            icon={<OrderedListOutlined />} 
                            onClick={showSummaryModal}
                        >
                            Özet Metrik Sıralama ve Görünürlük
                        </Button>
                    </div>
                )}
            />
            {renderSortModal()}
            {renderSummaryModal()}
        </div>
    );
};

export default MonthlyMetrics; 